import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ListPage,
  RefreshButton,
  StatCard,
} from '@skylabs-digital/backoffice-kit';
import { usePolling } from '@skylabs-digital/backoffice-kit/hooks';
import { AlertTriangle, Database, Gauge, Hourglass, Upload, Webhook } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProcessingActions } from '../api/processing.api';
import { useStorageActions } from '../api/storage.api';
import { useWebhookActions } from '../api/webhooks.api';
import { formatBytes } from '../components/apps/AppList';
import { formatDuration } from '../lib/format';
import type { ProcessingOverview } from '../schemas/processing.schema';
import type { StorageOverview, StorageSeries } from '../schemas/storage.schema';
import type { WebhooksOverview } from '../schemas/webhook.schema';

const REFRESH_MS = 30_000;

function todayPoint(series: StorageSeries | null) {
  return series?.points.at(-1) ?? null;
}

/** La home: cuatro números y un link a la sección que los explica. */
export function DashboardPage() {
  const processing = useProcessingActions();
  const storage = useStorageActions();
  const webhooks = useWebhookActions();
  const [proc, setProc] = useState<ProcessingOverview | null>(null);
  const [store, setStore] = useState<StorageOverview | null>(null);
  const [today, setToday] = useState<StorageSeries | null>(null);
  const [hooks, setHooks] = useState<WebhooksOverview | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, s, t, w] = await Promise.all([
        processing.overview({ window: '24h' }),
        storage.overview(),
        storage.series({ window: '7d' }),
        webhooks.overview({ window: '24h' }),
      ]);
      setProc(p);
      setStore(s);
      setToday(t);
      setHooks(w);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load the dashboard'));
    }
  }, [processing, storage, webhooks]);

  const { tick, running } = usePolling(load, REFRESH_MS);
  const day = todayPoint(today);
  const failureRate = hooks?.failureRate;

  return (
    <ListPage
      title="Dashboard"
      description="Fisto at a glance: the processing queue, storage, webhooks and today’s uploads."
      actions={<RefreshButton onRefresh={tick} isRefreshing={running} label={null} />}
    >
      <div className="space-y-6">
        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Processing</CardTitle>
              <Button asChild variant="link" className="h-auto p-0">
                <Link to="/processing">Open</Link>
              </Button>
            </CardHeader>
            <CardContent className="grid gap-3">
              <StatCard
                label="Queued"
                value={proc?.backlog ?? '—'}
                icon={<Hourglass />}
                tone={proc && !proc.workerAlive ? 'danger' : 'info'}
                hint={
                  proc === null
                    ? undefined
                    : !proc.workerAlive
                      ? 'No worker consuming'
                      : proc.etaSeconds === null
                        ? 'queue is empty'
                        : `ETA ${formatDuration(proc.etaSeconds)}`
                }
                loading={proc === null}
              />
              <StatCard
                label="Failed (24h)"
                value={proc?.failedInWindow ?? '—'}
                icon={<AlertTriangle />}
                tone={proc && proc.failedInWindow > 0 ? 'danger' : 'neutral'}
                loading={proc === null}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Storage</CardTitle>
              <Button asChild variant="link" className="h-auto p-0">
                <Link to="/storage">Open</Link>
              </Button>
            </CardHeader>
            <CardContent className="grid gap-3">
              <StatCard
                label="Used"
                value={formatBytes(store?.totalBytesUsed)}
                hint={store ? `${store.totalFiles} files in ${store.apps.length} apps` : undefined}
                icon={<Database />}
                loading={store === null}
              />
              <StatCard
                label="Apps near quota"
                value={store?.nearQuota.length ?? '—'}
                hint={
                  store && store.nearQuota.length > 0
                    ? store.nearQuota.map((a) => `${a.appName} ${a.quotaUsedPercent}%`).join(', ')
                    : '80% or more'
                }
                icon={<Gauge />}
                tone={store && store.nearQuota.length > 0 ? 'danger' : 'success'}
                loading={store === null}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Webhooks</CardTitle>
              <Button asChild variant="link" className="h-auto p-0">
                <Link to="/webhooks">Open</Link>
              </Button>
            </CardHeader>
            <CardContent className="grid gap-3">
              <StatCard
                label="Failed (24h)"
                value={hooks?.failed ?? '—'}
                hint={
                  failureRate === null || failureRate === undefined
                    ? undefined
                    : `${(failureRate * 100).toFixed(1)}% of ${(hooks?.delivered ?? 0) + (hooks?.failed ?? 0)}`
                }
                icon={<Webhook />}
                tone={hooks && hooks.failed > 0 ? 'danger' : 'success'}
                loading={hooks === null}
              />
              <StatCard
                label="Failing endpoints"
                value={hooks?.failing.length ?? '—'}
                hint={
                  hooks && hooks.failing.length > 0
                    ? hooks.failing.map((e) => `${e.appName}/${e.name}`).join(', ')
                    : undefined
                }
                icon={<AlertTriangle />}
                tone={hooks && hooks.failing.length > 0 ? 'danger' : 'neutral'}
                loading={hooks === null}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Today</CardTitle>
              <Button asChild variant="link" className="h-auto p-0">
                <Link to="/activity">Activity</Link>
              </Button>
            </CardHeader>
            <CardContent className="grid gap-3">
              <StatCard
                label="Uploads today"
                value={day?.uploadedFiles ?? '—'}
                hint={day ? formatBytes(day.uploadedBytes) : undefined}
                icon={<Upload />}
                loading={today === null}
              />
              <StatCard
                label="Pending uploads"
                value={store?.totalPending ?? '—'}
                hint={
                  store && store.totalStalePending > 0
                    ? `${store.totalStalePending} expired, not swept`
                    : undefined
                }
                icon={<Hourglass />}
                tone={store && store.totalStalePending > 0 ? 'warning' : 'neutral'}
                loading={store === null}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </ListPage>
  );
}
